import React, { useState } from "react";
import { Plus, Trash2, FileText, FileImage, File, ArrowLeft } from "lucide-react";
import axios from "axios";
import { BASE_URL } from "../Apis/apiConfig"; 
import Header from "./Header";
import Sidebar from "./Sidebar";
import SubHeader from "./SubHeader";
import "../Style/Rules.css";
import { useNavigate } from "react-router-dom";

const Rules = () => {
  const [rules, setRules] = useState([
    { header: "", text: "", image: null, pdf: null, previewImage: null, showText: false, showFiles: false }
  ]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const navigate = useNavigate();

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  const addRule = () => setRules([...rules, { header: "", text: "", image: null, pdf: null, previewImage: null, showText: false, showFiles: false }]);
  const removeRule = (index) => setRules(rules.filter((_, i) => i !== index));

  const toggleTextSection = (index) => {
    const newRules = [...rules];
    newRules[index].showText = !newRules[index].showText;
    setRules(newRules);
  };

  const toggleFilesSection = (index) => {
    const newRules = [...rules];
    newRules[index].showFiles = !newRules[index].showFiles;
    setRules(newRules);
  };

  const handleHeaderChange = (index, value) => {
    const newRules = [...rules];
    newRules[index].header = value;
    setRules(newRules);
  };

  const handleTextChange = (index, value) => {
    const newRules = [...rules];
    newRules[index].text = value;
    setRules(newRules);
  };

  const handleImageChange = (index, file) => {
    const newRules = [...rules];
    newRules[index].image = file;
    newRules[index].previewImage = URL.createObjectURL(file);
    setRules(newRules);
  };

  const handlePdfChange = (index, file) => {
    const newRules = [...rules];
    newRules[index].pdf = file;
    setRules(newRules);
  };

  const handleSubmit = async () => {
    try {
      const societyName = localStorage.getItem("society")
      const formData = new FormData();
      formData.append("societyName", societyName)

      rules.forEach((item) => {
        formData.append("ruleHeader", item.header);
        formData.append("rule", item.text);
      
        if (item.image) {
          formData.append("ruleImage", item.image);
        }

        if (item.pdf) {
          formData.append("rulePdf", item.pdf);
        }
      });

      const token = localStorage.getItem("token");
     
      const response = await axios.post(
        `${BASE_URL}/api/rules/addRules`,  
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: token ? `Bearer ${token}` : "",
          },
        }
      );

      console.log("SUCCESS:", response.data);
      alert("Rules submitted successfully!");

      // Reset form
      setRules([
        {
          header: "",
          text: "",
          image: null,
          pdf: null,
          previewImage: null,
          showText: false,
          showFiles: false,
        },
      ]);

    } catch (error) {
      console.error("ERROR:", error.response?.data || error.message);
      alert("Failed to submit rules");
    }
  };

  return (
    <div className="app-container">
      <Header toggleSidebar={toggleSidebar} />
      <div className="main-layout">
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
        <main className="content-area">
          <SubHeader />

          <div className="rules-form-container">

            {/* Back Button */}
            <button
  type="button"
  onClick={() => navigate(-1)} // or use navigate(previousPage) if you track previous page
  style={{
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px 16px",
    backgroundColor: "#4F46E5", // nice purple color
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontWeight: "500",
    cursor: "pointer",
    transition: "all 0.2s ease",
    marginBottom: "20px"
  }}
  onMouseEnter={e => e.currentTarget.style.backgroundColor = "#3730A3"} // darker on hover
  onMouseLeave={e => e.currentTarget.style.backgroundColor = "#4F46E5"} // original color
>
  <ArrowLeft size={18} /> Back
</button>

            {/* Header Section */}
            <div className="rules-header">
              <h1>Rules Management</h1>
              <p>Each rule has a header and a description. You can also add images or PDFs.</p>
            </div>

            {rules.map((rule, index) => (
              <div key={index} className="rule-card">

                {/* Action Buttons */}
                <div className="rule-actions">
                  <button onClick={() => toggleTextSection(index)} className="btn-action">
                    <FileText size={16} /> Add Text
                  </button>
                  <button onClick={() => toggleFilesSection(index)} className="btn-action">
                    <Plus size={16} /> Add Files
                  </button>
                </div>

                {/* Text Input */}
                {rule.showText && (
                  <div className="input-group full-width">
                    <input
                      type="text"
                      placeholder={`Header for Rule ${index + 1}`}
                      value={rule.header}
                      onChange={(e) => handleHeaderChange(index, e.target.value)}
                      className="rule-header-input"
                    />
                    <textarea
                      placeholder={`Description for Rule ${index + 1}`}
                      value={rule.text}
                      onChange={(e) => handleTextChange(index, e.target.value)}
                      rows={4}
                    />
                  </div>
                )}

                {/* File Upload Section */}
                {rule.showFiles && (
                  <div className="upload-section">
                    <label className="upload-button">
                      <FileImage size={18} /> Upload Image
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageChange(index, e.target.files[0])}
                        hidden
                      />
                    </label>
                    {rule.previewImage && <img src={rule.previewImage} alt="preview" className="preview-img" />}

                    <label className="upload-button">
                      <File size={18} /> Upload PDF
                      <input
                        type="file"
                        accept="application/pdf"
                        onChange={(e) => handlePdfChange(index, e.target.files[0])}
                        hidden
                      />
                    </label>
                    {rule.pdf && <span className="file-name">{rule.pdf.name}</span>}
                  </div>
                )}

                {/* Remove Rule */}
                <button type="button" onClick={() => removeRule(index)} className="btn-delete small-delete">
                  <Trash2 size={14} />
                </button>
              </div>
            ))}

            {/* Add / Submit Buttons */}
            <div className="form-actions">
              <button type="button" onClick={addRule} className="btn-add">
                <Plus size={16} /> Add Rule
              </button>
              <button type="button" onClick={handleSubmit} className="btn-submit">
                Submit All Rules
              </button>
            </div>

          </div>
        </main>
      </div>
    </div>
  );
};

export default Rules;